import React, { useContext, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import {
  Box,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import Input from '@material-ui/core/Input';
import { Autocomplete } from '@material-ui/lab';
import moment from 'moment';
import PropTypes from 'prop-types';

import { PersonContext } from '../../contexts/PersonContext';
import { create } from '../../feathersWrapper';
import { setJournalScope } from '../../functions/JournalFunctions';
import { getBalanceSheetAccountOptions, nameLabel } from '../Autocomplete/Library';
import Button from '../Button';
import { getManualBasicJournal } from '../SearchSelect/TransactionTypeOptions';
import TransactionScope from '../TransactionScope';

import AddRecurringTransactionFrequencyDialogContent from './AddRecurringTransactionFrequencyDialogContent';

const useStyles = makeStyles((theme) => ({
  dialogContentContainer: {
    [theme.breakpoints.down('md')]: {
      width: '425px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '600px',
    },
  },
  buttonContainer: {
    maxWidth: '400px',
    margin: 'auto',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
}));

export default function AddRecurringTransferDialogContent({ isOpen, closeDialog, onAddRecurringTransaction }) {
  const classes = useStyles();
  const context = useContext(PersonContext);
  const { organizationId, organizationName, bookkeepingCloseDate } = context;
  const component = {
    context: {
      organizationId,
    },
  };
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [key, setKey] = useState('transferTemplate');
  const [recurringTransactionName, setRecurringTransactionName] = useState('');
  const [frequency, setFrequency] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [journal, setJournal] = useState(getManualBasicJournal());
  const [balanceSheetAccountOptions, setBalanceSheetAccountOptions] = useState([]);

  useEffect(() => {
    const getData = async () => {
      const balanceSheetAccounts = await getBalanceSheetAccountOptions(component);
      setBalanceSheetAccountOptions(balanceSheetAccounts);
      setJournal({
        ...journal,
        journalScopeSelect: journal.journalScopeSelect || { name: `${organizationName} Portfolio`, id: 'Portfolio' },
      });
      setIsLoading(false);
    };
    getData();
  }, []);

  const addRecurringTransfer = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (bookkeepingCloseDate && moment(startDate).startOf('day').isSameOrBefore(bookkeepingCloseDate)) {
      setError({
        message: `The start date cannot be before the bookkeeping close date: ${moment(bookkeepingCloseDate).format('MM/DD/YYYY')}`,
      });
      return;
    }

    if (endDate && endDate < startDate) {
      setError({ message: 'The end date cannot occur before the start date' });
      return;
    }

    if (!moment(startDate).isValid()) {
      setError({ message: 'Please enter a valid start date' });
      return;
    }

    if (endDate && !moment(endDate).isValid()) {
      setError({ message: 'Please enter a valid end date' });
      return;
    }
    const journalSubmit = { ...journal };
    setJournalScope(journalSubmit);

    journalSubmit.organizationId = organizationId;
    journalSubmit.type = 'Transfer';

    journalSubmit.creditLines[0].credit = journalSubmit.amount;
    journalSubmit.debitLines[0].debit = journalSubmit.amount;
    journalSubmit.creditLines[0].accountId = journalSubmit.creditLines[0].accountIdSelect.id;
    journalSubmit.debitLines[0].accountId = journalSubmit.debitLines[0].accountIdSelect.id;

    journalSubmit.journalLines = journalSubmit.debitLines.concat(journalSubmit.creditLines);

    const dayOfMonth = Number(moment(startDate).format('D')) <= 28 ? Number(moment(startDate).format('D')) : 32;

    const recurringTransaction = {
      organizationId,
      journal: journalSubmit,
      name: recurringTransactionName,
      entityId: journalSubmit.entityId,
      propertyId: journalSubmit.propertyId,
      unitId: journalSubmit.unitId,
      type: journalSubmit.type,
      frequency: frequency.id,
      startDate,
      endDate,
      dayOfMonth,
      inactive: false,
    };

    setIsSubmitting(true);
    try {
      await create(component, 'recurring-transactions', recurringTransaction, true);
    } catch (err) {
      setError({ message: err.message });
      setIsSubmitting(false);
    }
    onAddRecurringTransaction();
    setIsSubmitting(false);
  };

  const getDialogContent = () => {
    switch (key) {
      case 'transferTemplate':
        return (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setKey('frequency');
            }}
          >
            <DialogTitle id="alert-dialog-title">Add Recurring Transfer (1 of 2)</DialogTitle>
            <DialogContent>
              <TextField
                label="Recurring Transaction Name"
                fullWidth
                margin="dense"
                required
                InputProps={{
                  value: recurringTransactionName,
                  name: 'recurringTransactionName',
                  onChange: (event) => {
                    setRecurringTransactionName(event.target.value);
                  },
                }}
              />
              <FormControl margin="dense" fullWidth>
                <InputLabel required>Amount</InputLabel>
                <NumberFormat
                  value={journal.amount}
                  required
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  onValueChange={(values) => {
                    const newJournal = { ...journal };
                    newJournal.amount = values.floatValue;
                    setJournal(newJournal);
                  }}
                  customInput={Input}
                />
              </FormControl>
              <TransactionScope
                journal={journal}
                transactionScopeChange={(newScopeValues) => {
                  const newJournal = { ...journal, ...newScopeValues };
                  setJournal(newJournal);
                }}
              />
              <Autocomplete
                options={balanceSheetAccountOptions}
                getOptionLabel={nameLabel}
                value={journal.creditLines[0].accountIdSelect}
                onChange={(_event, value) => {
                  const newJournal = { ...journal };
                  newJournal.creditLines[0].accountId = value.id;
                  newJournal.creditLines[0].accountIdSelect = value;
                  setJournal(newJournal);
                }}
                getOptionSelected={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Transferred From Account"
                    placeholder="Type to Search"
                    fullWidth
                    required
                  />
                )}
              />
              {journal.creditLines[0].accountIdSelect &&
                (journal.creditLines[0].accountIdSelect.plaidAccountId ||
                  journal.creditLines[0].accountIdSelect.yodleeAccountId) && (
                  <FormHelperText>
                    <Typography variant="inherit">
                      You've selected a linked account. Consider using a transaction matching rule to book from the
                      import feed instead of adding a recurring manual transfer here.
                    </Typography>
                  </FormHelperText>
                )}
              <Autocomplete
                options={balanceSheetAccountOptions}
                getOptionLabel={nameLabel}
                value={journal.debitLines[0].accountIdSelect}
                onChange={(_event, value) => {
                  const newJournal = { ...journal };
                  newJournal.debitLines[0].accountId = value.id;
                  newJournal.debitLines[0].accountIdSelect = value;
                  setJournal(newJournal);
                }}
                getOptionSelected={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Transferred To Account"
                    placeholder="Type to Search"
                    fullWidth
                    required
                  />
                )}
              />
              {journal.debitLines[0].accountIdSelect &&
                (journal.debitLines[0].accountIdSelect.plaidAccountId ||
                  journal.debitLines[0].accountIdSelect.yodleeAccountId) && (
                  <FormHelperText>
                    <Typography variant="inherit">
                      You've selected a linked account. Consider using a transaction matching rule to book from the
                      import feed instead of adding a recurring manual transfer here.
                    </Typography>
                  </FormHelperText>
                )}
              <TextField
                label="Description (optional)"
                fullWidth
                margin="dense"
                InputProps={{
                  value: journal.description,
                  name: 'nested_journal_description',
                  onChange: (event) => {
                    const newJournal = { ...journal };
                    newJournal.description = event.target.value;
                    setJournal(newJournal);
                  },
                }}
              />
              <Box className={classes.buttonContainer}>
                <Button hasFullWidth type="submit" variant="contained">
                  Continue
                </Button>
              </Box>
            </DialogContent>
          </form>
        );
      case 'frequency':
        return (
          <AddRecurringTransactionFrequencyDialogContent
            onAddRecurringTransaction={addRecurringTransfer}
            frequency={frequency}
            setFrequency={setFrequency}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            error={error}
            setError={setError}
            closeDialog={closeDialog}
            type="Transfer"
            onNavigateBack={() => {
              setKey('expenseTemplate');
            }}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return null;
  }

  return <Box className={classes.dialogContentContainer}>{isOpen && getDialogContent()}</Box>;
}

AddRecurringTransferDialogContent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  onAddRecurringTransaction: PropTypes.func.isRequired,
};
